import React, { Fragment, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import messages from "components/messagesDescription";
import moment from "moment";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { getDashboardActions } from "actions/dashboardActionsPP";
import { Button } from "cxa-components";
import { formatDateTime } from "../../../i18n/util";
import { setDelegatedByEmployeeKey } from "../../../actions/claimActionsPP";

const CountdownDisplay = ({ endDate, startDate, actionType }) => {
  const endMoment = moment(endDate);
  const now = moment();

  const calculateCountdown = modifiedDate => {
    const duration = moment.duration(modifiedDate - now);
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    return (
      <FormattedMessage
        {...messages.countdownText}
        values={{
          days: days,
          hours: hours,
          minutes: minutes,
        }}
      />
    );
  };

  // Set the time to 11:59 PM (23:59)
  const modifiedDate = endMoment.clone().set({ hour: 23, minute: 59, second: 59, millisecond: 59 });

  return (
    <Fragment>
      <p>
        <span className="action-primary-text action-deadline">
          {actionType === "ChangePassword" ? (
            <Fragment>{formatDateTime(endDate)}</Fragment>
          ) : (
            <Fragment>{formatDateTime(modifiedDate)}</Fragment>
          )}
        </span>
      </p>
      {actionType === "Enrolment" && (
        <p className="action-secondary-text-no-actions">{calculateCountdown(modifiedDate)}</p>
      )}
    </Fragment>
  );
};

const ActionItem = props => {
  const { intl, item, history } = props;
  const transformLink = action => {
    switch (action.actionType) {
      case "Enrolment":
        return `shop/enrolment`;
      case "HDF":
        return `${action.isHdfMobileResponsive ? "gen-hdf" : "hdf"}/${action.hdfId}`;
      default:
        return "";
    }
  };

  const handleButtonClick = item => {
    const link = transformLink(item);
    if (item.actionType === "HDF" && link) {
      window.open(link, "_blank");
    }
  };

  if (item.actionType === "Enrolment") {
    return (
      <Fragment>
        <hr className="separator" />
        <div>
          <p className="action-secondary-text">
            {intl.formatMessage(messages.benefitsEnrollmentActionDescription)}
          </p>
          <CountdownDisplay
            endDate={item.enrolmentSelectionEnd}
            startDate={item.enrolmentSelectionStart}
            actionType={item.actionType}
          />
          <Button
            primary
            onClick={() => history.push(transformLink(item))}
            className="action-button"
          >
            {intl.formatMessage(messages.benefitsEnrollmentButtonDescription)}
          </Button>
        </div>
      </Fragment>
    );
  }

  if (item.actionType === "HDF") {
    return (
      <Fragment>
        <hr className="separator" />
        <div>
          <Fragment>
            <p className="action-secondary-text">
              {intl.formatMessage(messages.HDFActionDescription)}
            </p>
            <CountdownDisplay
              endDate={item.hdfSubmissionEndDate || item.enrolmentSelectionEnd}
              startDate={item.enrolmentSelectionStart}
              actionType={item.actionType}
            />
            <p className="action-secondary-text">
              {intl.formatMessage(messages.HDFFailedToCompleteEnrolmentDescription)}
            </p>
          </Fragment>
          <Button primary onClick={() => handleButtonClick(item)} className="action-button">
            {intl.formatMessage(messages.HDFButtonDescription)}
          </Button>
        </div>
      </Fragment>
    );
  }

  return null;
};

const ActionsPP = ({ intl, history }) => {
  const dashboardActionsData = useSelector(state => state.dashboardPP.dashboardActions);

  const hasAdditionalActions = data => {
    if (!data || typeof data !== "object") return false;

    return Object.keys(data).some(key => key !== "totalNotificationsAction");
  };
  const hasAdditionalActionsCheck = hasAdditionalActions(dashboardActionsData);

  // expense claim delegated sorting data function
  const uniqueByEmployeeKey = data => {
    const seen = new Set();
    return data.filter(item => {
      if (seen.has(item.employeeKey)) return false;
      seen.add(item.employeeKey);
      return true;
    });
  };

  const sortingExpenseDelegated =
    dashboardActionsData &&
    Array.isArray(dashboardActionsData.delegatedClaimsActions) &&
    dashboardActionsData.delegatedClaimsActions.length > 0
      ? uniqueByEmployeeKey(dashboardActionsData.delegatedClaimsActions)
      : [];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDashboardActions());
  }, []);

  if (!hasAdditionalActionsCheck) {
    return (
      <div className="actions-container tile-border">
        <h3>{intl.formatMessage(messages.actionsDescription)}</h3>
        <p className="action-secondary-text-no-actions">
          {intl.formatMessage(messages.noPendingActionsDescription)}
        </p>
      </div>
    );
  }

  const transformLink = action => {
    switch (action.actionType) {
      case "Claim":
        return `${action.redirectModule.toLowerCase() + "/documents/" + action.claimObjectId}`;
      default:
        return "";
    }
  };

  function getDayNumbers(expiryDate) {
    const expiryDateFormat = new Date(expiryDate);
    const now = new Date();

    expiryDateFormat.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    return Math.max(0, Math.floor((expiryDateFormat - now) / (1000 * 60 * 60 * 24)));
  }

  const translateFields = fields => {
    return fields
      .map(field => intl.formatMessage({ id: field.id, defaultMessage: field.name }))
      .join(", ");
  };

  return (
    <div className="actions-container tile-border">
      <h3>{intl.formatMessage(messages.actionsDescription)}</h3>

      {/* Benefits Actions */}
      {dashboardActionsData.benefitsActions &&
        dashboardActionsData.benefitsActions.map((item, index) => (
          <ActionItem key={index} intl={intl} item={item} history={history} />
        ))}

      {/* Claims Actions */}
      {dashboardActionsData.claimsActions &&
        dashboardActionsData.claimsActions.map(item => {
          return (
            <Fragment key={item.claimObjectId}>
              <hr className="separator" />
              <div>
                <p className="action-secondary-text">
                  {intl.formatMessage(messages.claimActionDescription)}
                </p>
                <p>
                  <span className="action-primary-text action-highlight">
                    {intl.formatMessage(messages.claimIdDescription)}
                  </span>{" "}
                  <span className="action-secondary-text-claim-id action-highlight">
                    {item.claimDocumentId}
                  </span>
                </p>
                <p>
                  <span className="action-primary-text action-highlight">
                    {intl.formatMessage(messages.claimItemDescription)}
                  </span>{" "}
                  <span className="action-secondary-text-claim-id action-highlight">
                    {" "}
                    {intl.formatMessage({
                      id: item.claimItemName,
                      defaultMessage: item.claimItemName,
                    })}
                  </span>
                </p>
                <Button
                  primary
                  onClick={() => history.push(transformLink(item))}
                  className="action-button"
                >
                  {intl.formatMessage(messages.claimButtonDescription)}
                </Button>
              </div>
            </Fragment>
          );
        })}

      {sortingExpenseDelegated.map(item => {
        return (
          <Fragment key={item.claimDocumentId}>
            <hr className="separator" />
            <div>
              <p>
                {intl.formatMessage(messages.pendingReview)}
                <br />
                <span className="action-primary-text action-highlight">{item.claimantName}</span>
              </p>
              <Button
                primary
                onClick={() => {
                  history.push(`/claims/track`);
                  dispatch(setDelegatedByEmployeeKey(item.employeeKey.toString()));
                }}
                className="action-button"
              >
                {intl.formatMessage(messages.claims.reviewClaimDescription)}
              </Button>
            </div>
          </Fragment>
        );
      })}

      {dashboardActionsData.passwordExpiryAction && (
        <div>
          <hr className="separator" />
          <p className="action-secondary-text">{intl.formatMessage(messages.reminderResetPass)}</p>
          <CountdownDisplay
            endDate={dashboardActionsData.passwordExpiryAction.expiryDate}
            startDate={moment().format()}
            actionType={dashboardActionsData.passwordExpiryAction.actionType}
          />
          <p>
            <span className="action-secondary-text">
              {intl
                .formatMessage({
                  id: "config.action.passwordExpiryDate",
                  defaultMessage: dashboardActionsData.passwordExpiryAction.message,
                })
                .replace(
                  "[PasswordExpireDays]",
                  getDayNumbers(dashboardActionsData.passwordExpiryAction.expiryDate)
                )}
            </span>{" "}
          </p>
          <Button primary onClick={() => history.push("/changePassword")} className="action-button">
            {intl.formatMessage(messages.changePasswordDescription)}
          </Button>
        </div>
      )}

      {dashboardActionsData.missingInfoAction && (
        <div>
          <hr className="separator" />
          <p>
            <span className="action-secondary-text" style={{ whiteSpace: "pre-line" }}>
              {intl
                .formatMessage({
                  id: "action.missingInfo",
                  defaultMessage: dashboardActionsData.missingInfoAction.message,
                })
                .replace(
                  "[MissingFields]",
                  translateFields(dashboardActionsData.missingInfoAction.fields)
                )}
            </span>
          </p>
          <Button
            primary
            onClick={() => {
              history.push("/userProfile/edit");
            }}
            className="action-button"
          >
            {intl.formatMessage(messages.updateDescription)}
          </Button>
        </div>
      )}

      {dashboardActionsData.additionOfSpouseAction && (
        <div>
          <hr className="separator" />
          <p className="action-secondary-text">{intl.formatMessage(messages.addSpouseTitle)}</p>
          <p>
            <span className="action-secondary-text">
              {intl.formatMessage({
                id: "config.action.additionOfSpouse",
                defaultMessage: dashboardActionsData.additionOfSpouseAction.message,
              })}
            </span>{" "}
          </p>
          <Button
            primary
            onClick={() => {
              history.push("/userProfile");
            }}
            className="action-button"
          >
            {intl.formatMessage(messages.reviewDescription)}
          </Button>
        </div>
      )}
    </div>
  );
};

ActionItem.propTypes = {
  intl: intlShape.isRequired,
  item: PropTypes.shape({
    actionType: PropTypes.string,
    hdfSubmissionEndDate: PropTypes.string,
    enrolmentSelectionEnd: PropTypes.string,
    enrolmentSelectionStart: PropTypes.string,
  }),
  history: PropTypes.object,
};

CountdownDisplay.propTypes = {
  endDate: PropTypes.string,
  startDate: PropTypes.string,
  actionType: PropTypes.string,
};

ActionsPP.propTypes = {
  history: PropTypes.object,
  intl: intlShape.isRequired,
};

export default withRouter(injectIntl(ActionsPP));



// WEBPACK FOOTER //
// ./components/dashboard/DashboardNewUI/ActionsPP.js